const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

export const WERSEE_ID_ORIGIN = trimTrailingSlash(
  import.meta.env.VITE_ID_BASE_URL
  || import.meta.env.VITE_AUTH_BASE_URL
  || 'https://id.wersee.com',
);

// Backwards-compatible name for callers that still describe the central
// identity entry point as "auth". Wersee ID is the canonical origin.
export const WERSEE_AUTH_ORIGIN = WERSEE_ID_ORIGIN;

export const WERSEE_APP_ORIGIN = trimTrailingSlash(
  import.meta.env.VITE_APP_BASE_URL
  || import.meta.env.VITE_SITE_URL
  || import.meta.env.VITE_PUBLIC_SITE_URL
  || 'https://www.wersee.com',
);

const trustedWerseeHosts = new Set([
  'wersee.com',
  'www.wersee.com',
  'id.wersee.com',
  'auth.wersee.com',
  'app.wersee.com',
  'ai.wersee.com',
  'billing.wersee.com',
  'blog.wersee.com',
  'cloud.wersee.com',
  'docs.wersee.com',
  'email.wersee.com',
  'files.wersee.com',
  'community.wersee.com',
  'chat.wersee.com',
  'gate.wersee.com',
  'mail.wersee.com',
  'pay.wersee.com',
  'lp.wersee.com',
  'upc.wersee.com',
]);

const landingPageHostname = 'lp.wersee.com';

export const isLocalAuthHostname = (hostname: string) => {
  const normalized = hostname.toLowerCase();
  return normalized === 'localhost'
    || normalized === '127.0.0.1'
    || normalized === '[::1]'
    || normalized.endsWith('.localhost')
    || normalized.endsWith('.local');
};

export const isAuthHostname = (hostname: string) => {
  const normalized = hostname.toLowerCase();
  return normalized === 'id.wersee.com'
    || normalized === 'auth.wersee.com'
    || normalized === 'id.wersee.localhost'
    || normalized === 'auth.wersee.localhost';
};

export const isIdentityHostname = (hostname: string) => {
  const normalized = hostname.toLowerCase();
  return normalized === 'id.wersee.com' || normalized === 'id.wersee.localhost';
};

export const isCentralAuthPath = (pathname: string) => (
  pathname === '/auth'
  || pathname.startsWith('/auth/')
  || pathname === '/signin'
  || pathname === '/login'
  || pathname === '/sign-in'
  || pathname === '/log-in'
  || pathname === '/forgot-password'
  || pathname === '/update-password'
  || pathname === '/confirm-email'
  || pathname === '/logged-out'
  || pathname === '/verify'
  || pathname === '/oauth/consent'
  || pathname.startsWith('/oauth/apps/')
);

export const isTrustedWerseeReturnOrigin = (origin: string) => {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocalAuthHostname(url.hostname))) {
      return false;
    }
    return trustedWerseeHosts.has(url.hostname.toLowerCase()) || isLocalAuthHostname(url.hostname);
  } catch {
    return false;
  }
};

const browserOrigin = () => (
  typeof window === 'undefined' ? WERSEE_APP_ORIGIN : window.location.origin
);

export const getAuthOrigin = () => {
  if (typeof window === 'undefined') return WERSEE_AUTH_ORIGIN;
  return import.meta.env.DEV || isLocalAuthHostname(window.location.hostname)
    ? window.location.origin
    : WERSEE_AUTH_ORIGIN;
};

export const getDefaultAppOrigin = () => {
  if (typeof window === 'undefined') return WERSEE_APP_ORIGIN;
  return import.meta.env.DEV || isLocalAuthHostname(window.location.hostname)
    ? window.location.origin
    : WERSEE_APP_ORIGIN;
};

const normalizedReturnPath = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

export const resolveAuthReturnUrl = (rawValue?: string | null, fallbackPath = '/workspace') => {
  const raw = String(rawValue || fallbackPath).trim();
  const currentOrigin = browserOrigin();
  const relativeBase = isAuthHostname(new URL(currentOrigin).hostname)
    ? getDefaultAppOrigin()
    : currentOrigin;

  try {
    const candidate = new URL(raw, relativeBase);
    if (!isTrustedWerseeReturnOrigin(candidate.origin)) {
      return `${getDefaultAppOrigin()}${fallbackPath}`;
    }

    if (candidate.hostname.toLowerCase() === landingPageHostname) {
      return `${getDefaultAppOrigin()}/`;
    }

    const path = normalizedReturnPath(candidate);
    if (isAuthHostname(candidate.hostname) || /^\/(?:auth(?:\/|$)|signin\/?$|login\/?$|forgot-password\/?$|update-password\/?$|confirm-email\/?$)/i.test(candidate.pathname)) {
      return `${getDefaultAppOrigin()}${fallbackPath}`;
    }

    return `${candidate.origin}${path || '/'}`;
  } catch {
    return `${getDefaultAppOrigin()}${fallbackPath}`;
  }
};

export const resolveInternalAuthReturnUrl = (rawValue?: string | null) => {
  if (!rawValue) return null;
  try {
    const candidate = new URL(rawValue, getAuthOrigin());
    const isIdentityAccount = /^\/account(?:\/|$)/.test(candidate.pathname);
    const isConsent = candidate.pathname === '/oauth/consent'
      && /^[A-Za-z0-9_-]{8,512}$/.test(candidate.searchParams.get('authorization_id') || '');
    const isPermissions = /^\/oauth\/apps\/[0-9a-f-]{36}\/permissions$/i.test(candidate.pathname)
      && /^[A-Za-z0-9_-]{8,512}$/.test(candidate.searchParams.get('authorization_id') || '');
    if (candidate.origin !== getAuthOrigin() || (!isIdentityAccount && !isConsent && !isPermissions)) return null;
    return candidate.toString();
  } catch {
    return null;
  }
};

export const returnPathForAuthFlow = (returnUrl: string) => {
  try {
    const url = new URL(returnUrl);
    return normalizedReturnPath(url) || '/workspace';
  } catch {
    return '/workspace';
  }
};

export const buildAuthUrl = (path = '/auth') => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getAuthOrigin()}${normalizedPath}`;
};

export const buildAuthEntryUrl = (path = '/auth', returnUrl?: string | null) => {
  const target = new URL(buildAuthUrl(path));
  if (returnUrl) target.searchParams.set('returnUrl', resolveAuthReturnUrl(returnUrl));
  return target.toString();
};
